<template>
  <div class="light-dashboard pt-2 px-3" style="position: relative;">
    <div v-if="loading" class="dashboard-loading-overlay">
      <v-progress-circular
        indeterminate
        color="success"
        size="60"
      ></v-progress-circular>
    </div>
    <!-- Header -->
    <div class="dash-header">
      <div class="dash-header-left">
        <img :src="logo" class="dash-logo" />
        <div>
          <div class="dash-title">UTA User Portal Dashboard</div>
          <div class="dash-subtitle">Welcome, {{ user }}</div>
        </div>
      </div>
      <div class="dash-header-right">
        <v-icon
          title="Log Out"
          @click.prevent="logOut"
          small
          color="red"
          class="ml-1"
          >mdi-logout</v-icon
        >
      </div>
    </div>

    <!-- 2-column grid -->
    <v-layout row wrap class="dash-grid">
      <!-- ───────── RIGHT COLUMN ───────── -->
      <v-flex xs12 md8 class="dash-col pr-2">
        <!-- IT Help Desk -->
        <div class="dark-card mb-3">
          <div class="card-header">
            <v-icon color="#5cb860" small class="mr-2"
              >mdi-help-circle-outline</v-icon
            >
            <span class="card-title-text">IT Help Desk</span>
          </div>

          <div class="helpdesk-body">
            <!-- Donut chart -->
            <div class="donut-wrapper">
              <svg width="90" height="90" viewBox="0 0 100 100">
                <circle
                  cx="50"
                  cy="50"
                  r="38"
                  fill="none"
                  stroke="#e0e0e0"
                  stroke-width="12"
                />
                <circle
                  cx="50"
                  cy="50"
                  r="38"
                  fill="none"
                  stroke="#5cb860"
                  stroke-width="12"
                  :stroke-dasharray="closedArcLen + ' ' + (239 - closedArcLen)"
                  stroke-dashoffset="0"
                  stroke-linecap="butt"
                  transform="rotate(-90 50 50)"
                >
                  <title>Closed tickets: {{ closedTicketsCount }}</title>
                </circle>
                <circle
                  cx="50"
                  cy="50"
                  r="38"
                  fill="none"
                  stroke="#1976d2"
                  stroke-width="12"
                  :stroke-dasharray="openArcLen + ' ' + (239 - openArcLen)"
                  :stroke-dashoffset="-closedArcLen"
                  stroke-linecap="butt"
                  transform="rotate(-90 50 50)"
                >
                  <title>Open tickets: {{ openTicketsCount }}</title>
                </circle>
                <circle
                  cx="50"
                  cy="50"
                  r="38"
                  fill="none"
                  stroke="#ffa21a"
                  stroke-width="12"
                  :stroke-dasharray="holdArcLen + ' ' + (239 - holdArcLen)"
                  :stroke-dashoffset="-(closedArcLen + openArcLen)"
                  stroke-linecap="butt"
                  transform="rotate(-90 50 50)"
                >
                  <title>On hold tickets: {{ onHoldTicketsCount }}</title>
                </circle>
                <text
                  x="50"
                  y="47"
                  text-anchor="middle"
                  fill="#424242"
                  font-size="13"
                  font-weight="bold"
                >
                  {{ totalTickets }}
                </text>
                <text
                  x="50"
                  y="60"
                  text-anchor="middle"
                  fill="#9e9e9e"
                  font-size="11"
                  font-weight="bold"
                >
                  tickets
                </text>
              </svg>
            </div>

            <!-- Progress bars -->
            <div class="progress-section">
              <div
                title="Click to view open tickets"
                class="progress-row"
                @click="openRouteInNewTab('HelpDeskRequests')"
              >
                <span class="ldot blue"></span>
                <span class="progress-label"
                  >Open ({{ openTicketsCount }})</span
                >
                <div class="progress-track">
                  <div
                    class="progress-fill blue"
                    :style="{ width: openTicketsPct + '%' }"
                  ></div>
                </div>
                <span class="progress-pct">{{ openTicketsPct }}%</span>
              </div>
              <div
                title="Click to view on hold tickets"
                class="progress-row"
                @click="openRouteInNewTab('HelpDeskRequests')"
              >
                <span class="ldot orange"></span>
                <span class="progress-label"
                  >On Hold ({{ onHoldTicketsCount }})</span
                >
                <div class="progress-track">
                  <div
                    class="progress-fill orange"
                    :style="{ width: onHoldTicketsPct + '%' }"
                  ></div>
                </div>
                <span class="progress-pct">{{ onHoldTicketsPct }}%</span>
              </div>
              <div
                title="Click to view closed tickets"
                class="progress-row"
                @click="
                  openRouteInNewTab('HelpDeskRequests', { showClosed: true })
                "
              >
                <span class="ldot green"></span>
                <span class="progress-label"
                  >Closed ({{ closedTicketsCount }})</span
                >
                <div class="progress-track">
                  <div
                    class="progress-fill green"
                    :style="{ width: closedTicketsPct + '%' }"
                  ></div>
                </div>
                <span class="progress-pct">{{ closedTicketsPct }}%</span>
              </div>
            </div>
          </div>

          <div class="card-actions">
            <button
              class="btn-green small"
              @click="openRouteInNewTab('HelpDeskRequests')"
              title="Click to view tickets"
            >
              View Tickets
            </button>
            <button
              class="btn-green small"
              @click="
                openRouteInNewTab('HelpDeskRequests', { openNewTicket: true })
              "
              title="Click to create a new ticket"
            >
              New Ticket
            </button>
          </div>
        </div>

        <!-- Shared Files -->
        <div class="dark-card mb-3">
          <div class="card-header">
            <v-icon color="#5cb860" small class="mr-2">mdi-file-move</v-icon>
            <span class="card-title-text">Shared Files</span>
          </div>
          <div class="shared-stats">
            <div
              class="stat-row"
              title="Click to view files shared with you"
              @click="openRouteInNewTab('FilesSharedWithUser')"
            >
              <span class="stat-label">
                <v-icon small color="#1976d2" class="mr-1"
                  >mdi-account-arrow-left</v-icon
                >
                Shared With You
              </span>
              <a href="/files-shared-with-user" class="stat-link">{{
                sharedWithYouCount
              }}</a>
            </div>
            <div
              class="stat-row"
              title="Click to view files shared by you"
              @click="openRouteInNewTab('SharedFiles')"
            >
              <span class="stat-label">
                <v-icon small color="#5cb860" class="mr-1"
                  >mdi-account-arrow-right</v-icon
                >
                Shared By You
              </span>
              <a href="/shared-files" class="stat-link green-link">{{
                sharedByYouCount
              }}</a>
            </div>
          </div>
          <div class="file-actions-bar mt-3">
            <button
              class="btn-green"
              @click="openRouteInNewTab('SharedFiles', { shareNewFile: true })"
            >
              Share New File
            </button>
          </div>
        </div>

        <!-- Pay UTA widget -->
        <v-layout row wrap>
          <v-flex xs12 md6 class="pr-1">
            <div class="dark-card">
              <div class="card-header">
                <v-icon color="#5cb860" small class="mr-2"
                  >mdi-credit-card</v-icon
                >
                <span class="card-title-text">Pay UTA</span>
              </div>
              <div class="pay-actions">
                <button
                  class="btn-green"
                  @click="openRouteInNewTab('CustomPayment', { mode: 'basic' })"
                >
                  <v-icon small class="mr-1">mdi-credit-card-outline</v-icon
                  >Make a Payment
                </button>
              </div>
            </div>
          </v-flex>
          <v-flex xs12 md6 class="pl-1">
            <div class="dark-card">
              <div class="card-header">
                <v-icon color="#5cb860" small class="mr-2">mdi-calendar</v-icon>
                <span class="card-title-text">Calendar</span>
              </div>
              <div class="pay-actions">
                <button
                  class="btn-green"
                  @click="openRouteInNewTab('Calendar')"
                >
                  <v-icon small class="mr-1">mdi-calendar-month-outline</v-icon
                  >View Calendar
                </button>
              </div>
            </div>
          </v-flex>
        </v-layout>
      </v-flex>

      <!-- ───────── SECOND COLUMN  ───────── -->
      <v-flex xs12 md4 class="dash-col pl-2">
        <div class="dark-card full-height">
          <div class="tab-bar">
            <span
              :class="['tab-btn', activeTab === 'manuals' ? 'active' : '']"
              @click="activeTab = 'manuals'"
            >
              <v-icon
                :color="activeTab === 'manuals' ? '#5cb860' : '#9e9e9e'"
                small
                class="mr-2"
                >mdi-book-open-page-variant</v-icon
              >UTA User manuals</span
            >
            <span
              style="direction: rtl;"
              :class="['tab-btn', activeTab === 'info' ? 'active' : '']"
              @click="activeTab = 'info'"
            >
              <v-icon
                :color="activeTab === 'info' ? '#5cb860' : '#9e9e9e'"
                small
                class="mr-2"
                >mdi-book-open-page-variant</v-icon
              >
              חודש'ליכע עלטערן אינפארמאציע בלעטל</span
            >
          </div>

          <div class="search-bar">
            <v-icon small color="#94a3b8" class="mr-1">mdi-magnify</v-icon>
            <input
              v-model="searchQuery"
              type="text"
              class="search-input"
              placeholder="Search files..."
              clearable
            />
          </div>

          <!-- UTA Manuals tab -->
          <div v-if="activeTab === 'info'" class="file-list">
            <div
              v-for="(doc, i) in filtered"
              :key="doc.id || i"
              class="doc-item"
              @click="openPublication(doc)"
            >
              <v-icon color="#f62017" small class="mr-2">mdi-file-pdf</v-icon>
              <div class="doc-info">
                <div class="doc-name">{{ doc.description }}</div>
              </div>
              <v-icon small color="#5cb860">mdi-eye</v-icon>
            </div>
            <div v-if="filtered.length === 0" class="empty-state">
              <v-icon color="#475569" class="mb-2"
                >mdi-file-search-outline</v-icon
              >
              <div>No documents available</div>
            </div>
          </div>

          <div v-if="activeTab === 'manuals'" class="file-list">
            <div
              v-for="(pub, i) in filtered"
              :key="pub.id || i"
              class="doc-item"
              @click="openPublication(pub)"
            >
              <v-icon color="#f62017" small class="mr-2">mdi-file-pdf</v-icon>
              <div class="doc-info">
                <div class="doc-name">{{ pub.description }}</div>
              </div>
              <v-icon small color="#5cb860">mdi-eye</v-icon>
            </div>
            <div v-if="filtered.length === 0" class="empty-state">
              <v-icon color="#475569" class="mb-2"
                >mdi-book-search-outline</v-icon
              >
              <div>No manuals found</div>
            </div>
          </div>
        </div>
      </v-flex>
    </v-layout>
  </div>
</template>

<script>
export default {
  name: "Dashboard",
  metaInfo: {
    title: "Dashboard",
  },
  data() {
    return {
      logo: "https://utaw.blob.core.windows.net/images/logo+uta11.png",

      openTicketsCount: 0,
      onHoldTicketsCount: 0,
      closedTicketsCount: 0,
      sharedWithYouCount: 0,
      sharedByYouCount: 0,

      user: "",
      parentInfoPublications: [],
      userManualsPublications: [],

      activeTab: "manuals",
      searchQuery: "",

      loading: true,
    };
  },
  computed: {
    totalTickets() {
      return (
        this.openTicketsCount +
        this.onHoldTicketsCount +
        this.closedTicketsCount
      );
    },
    openTicketsPct() {
      if (!this.totalTickets) return 0;
      return Math.round((this.openTicketsCount / this.totalTickets) * 100);
    },
    onHoldTicketsPct() {
      if (!this.totalTickets) return 0;
      return Math.round((this.onHoldTicketsCount / this.totalTickets) * 100);
    },
    closedTicketsPct() {
      if (!this.totalTickets) return 0;
      return Math.round((this.closedTicketsCount / this.totalTickets) * 100);
    },
    closedArcLen() {
      return Math.round((this.closedTicketsPct / 100) * 239);
    },
    openArcLen() {
      return Math.round((this.openTicketsPct / 100) * 239);
    },
    holdArcLen() {
      return Math.round((this.onHoldTicketsPct / 100) * 239);
    },
    filtered() {
      if (!this.searchQuery && this.activeTab === "manuals")
        return this.userManualsPublications;
      if (!this.searchQuery && this.activeTab === "info")
        return this.parentInfoPublications;
      const q = this.searchQuery.toLowerCase();
      if (this.activeTab === "manuals")
        return this.userManualsPublications.filter(
          (m) => m.description && m.description.toLowerCase().includes(q)
        );
      if (this.activeTab === "info")
        return this.parentInfoPublications.filter(
          (p) => p.description && p.description.toLowerCase().includes(q)
        );
      return [];
    },
  },
  created() {
    this.user = this.$store.state.authentication.user;
    this.loadDashboardInfo();
  },
  methods: {
    async loadDashboardInfo() {
      try {
        const info = await this.$http.get("api/adminPortal/getDashboardInfo");
        this.openTicketsCount = info.openRequests;
        this.onHoldTicketsCount = info.onHoldRequests;
        this.closedTicketsCount = info.closedRequests;
        this.sharedWithYouCount = info.filesSharedWithUser;
        this.sharedByYouCount = info.sharedFiles;
        this.parentInfoPublications = info.parentInfoPublications;
        this.userManualsPublications = info.userManualsPublications;
      } catch (e) {
        // counts remain 0
      } finally {
        this.loading = false;
      }
    },
    openPublication(publication) {
      window.open(publication.fileURL);
    },
    openRouteInNewTab(route, query = {}) {
      this.$router.push({ name: route, query });
    },
    logOut() {
      this.$router.push({
        name: "Login",
        query: { redirect: this.$route.fullPath },
      });
    },
  },
};
</script>

<style scoped>
/* ══════════════════════════════════
     Light theme – matches site palette
     ══════════════════════════════════ */

/* ── Base ── */
.light-dashboard {
  background-color: #eee;
  min-height: 100vh;
  color: #424242;
  font-family: "Roboto", "Segoe UI", sans-serif;
  zoom: 1.25;
}
.dashboard-loading-overlay {
  position: absolute;
  inset: 0;
  z-index: 10;
  background: rgba(255, 255, 255, 0.7);
  display: flex;
  align-items: flex-start;
  padding-top: 15%;
  justify-content: center;
}

/* ── Header ── */
.dash-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 20px;
  background: #fff;
  border-bottom: 1px solid #e0e0e0;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.08);
}
.dash-header-left {
  display: flex;
  align-items: center;
  gap: 14px;
}
.dash-logo {
  height: 40px;
  width: auto;
}
.dash-title {
  font-size: 22px;
  font-weight: 400;
  color: #424242;
}
.dash-subtitle {
  font-size: 13px;
  color: #757575;
  margin-top: 2px;
}

/* ── Grid ── */
.dash-grid {
  padding-top: 12px !important;
}
.dash-col {
  display: flex;
  flex-direction: column;
}

/* ── Card ── */
.dark-card {
  background: #fff;
  border-radius: 4px;
  padding: 16px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.12), 0 1px 2px rgba(0, 0, 0, 0.08);
}
.full-height {
  flex: 1;
}
.mb-3 {
  margin-bottom: 10px !important;
}

/* ── Card Header ── */
.card-header {
  display: flex;
  align-items: center;
  margin-bottom: 12px;
  padding-bottom: 8px;
  border-bottom: 1px solid #e0e0e0;
}
.card-title-text {
  font-size: 13px;
  font-weight: 500;
  color: #616161;
  text-transform: uppercase;
  letter-spacing: 0.6px;
}

.doc-item {
  display: flex;
  align-items: center;
  padding: 7px 8px;
  border-radius: 4px;
  background: #fafafa;
  border: 1px solid #f0f0f0;
  cursor: pointer;
  transition: background 0.15s;
  gap: 8px;
}
.doc-item:hover {
  background: #e3f2fd;
}
.doc-info {
  flex: 1;
  min-width: 0;
}
.doc-name {
  font-size: 12px;
  font-weight: 500;
  color: #000000;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* ── Tab Bar ── */
.tab-bar {
  display: flex;
  margin-bottom: 10px;
  border-bottom: 2px solid #e0e0e0;
}
.tab-btn {
  padding: 8px 16px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  color: #9e9e9e;
  border-bottom: 2px solid transparent;
  margin-bottom: -2px;
  transition: all 0.2s;
  white-space: normal;
}
.tab-btn.active {
  color: #5cb860;
  border-bottom-color: #5cb860;
}

/* ── Search Bar ── */
.search-bar {
  display: flex;
  align-items: center;
  background: #fafafa;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  padding: 6px 10px;
  margin-bottom: 10px;
}
.search-input {
  background: transparent;
  border: none;
  outline: none;
  color: #424242;
  font-size: 13px;
  width: 100%;
}
.search-input::placeholder {
  color: #bdbdbd;
}

/* ── File List ── */
.file-list {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

/* ── Shared Stats ── */
.shared-stats {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 4px 0;
}
.stat-row {
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 10px;
  background: #fafafa;
  border: 1px solid #f0f0f0;
  border-radius: 4px;
}
.stat-label {
  display: flex;
  align-items: center;
  font-size: 13px;
  color: #616161;
  font-weight: 500;
}
.stat-link {
  font-size: 18px;
  font-weight: 700;
  color: #1976d2;
  text-decoration: none;
}
.stat-link.green-link {
  color: #5cb860;
}
.file-actions-bar {
  padding-top: 4px;
}

.btn-green {
  padding: 6px 14px;
  border-radius: 3px;
  font-size: 12px;
  font-weight: 500;
  background: #5cb860;
  color: #fff;
  border: none;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  transition: background 0.15s;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
}
.btn-green:hover {
  background: #4caf50;
}
.btn-green.small {
  padding: 5px 10px;
  font-size: 11px;
}

/* ── Card Actions ── */
.card-actions {
  display: flex;
  gap: 8px;
  margin-top: 10px;
  padding-top: 10px;
}

/* ── Help Desk Body ── */
.helpdesk-body {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  margin-bottom: 4px;
}

/* ── Donut Chart ── */
.donut-wrapper {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
}
.ldot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex-shrink: 0;
}
.ldot.green {
  background: #5cb860;
}
.ldot.blue {
  background: #1976d2;
}
.ldot.orange {
  background: #ffa21a;
}

/* ── Progress Section ── */
.progress-section {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 8px;
  justify-content: center;
}
.progress-row {
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 7px;
}
.progress-label {
  font-size: 12px;
  color: #616161;
  width: 90px;
  flex-shrink: 0;
  font-weight: 500;
}
.progress-track {
  flex: 1;
  height: 6px;
  background: #e0e0e0;
  border-radius: 3px;
  overflow: hidden;
}
.progress-fill {
  height: 100%;
  border-radius: 3px;
  transition: width 0.6s ease;
  min-width: 4px;
}
.progress-fill.green {
  background: #5cb860;
}
.progress-fill.blue {
  background: #1976d2;
}
.progress-fill.orange {
  background: #ffa21a;
}
.progress-pct {
  font-size: 11px;
  color: #9e9e9e;
  width: 30px;
  text-align: right;
  flex-shrink: 0;
}

/* ── Pay Actions ── */
.pay-actions {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

/* ── Empty State ── */
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 24px 0;
  color: #9e9e9e;
  font-size: 13px;
}
</style>
